// src/services/dashboardService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const BASE_URL = `${API_URL}/dashboard`;

// Get the auth token from localStorage
const getAuthToken = () => localStorage.getItem('token');

// Set the Authorization header with the token
const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${getAuthToken()}`
  }
});

/**
 * Get financial summary data with period filtering
 * @param {Object} params - Query parameters
 * @param {string} params.period - Time period (1day, 7days, 30days, 12months, custom)
 * @param {string} [params.customStart] - Start date for custom period (YYYY-MM-DD)
 * @param {string} [params.customEnd] - End date for custom period (YYYY-MM-DD)
 * @returns {Promise<Object>} Financial summary data
 */
export const getFinancialSummary = async (params = { period: '30days' }) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add all params to query string
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value);
    });
    
    const response = await axios.get(
      `${BASE_URL}/financial-summary?${queryParams.toString()}`,
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching financial summary:', error);
    throw error;
  }
};

/**
 * Get recent phone calls
 * @returns {Promise<Array>} Recent calls data
 */
export const getRecentCalls = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/recent-calls`,
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching recent calls:', error);
    throw error;
  }
};

/**
 * Get recent SMS messages
 * @returns {Promise<Array>} Recent SMS data
 */
export const getRecentSms = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/recent-sms`,
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching recent SMS:', error);
    throw error;
  }
};

/**
 * Get recent leads
 * @returns {Promise<Array>} Recent leads data
 */
export const getRecentLeads = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/recent-leads`,
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching recent leads:', error);
    throw error;
  }
};

/**
 * Get upcoming jobs
 * @returns {Promise<Array>} Upcoming jobs data
 */
export const getUpcomingJobs = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/upcoming-jobs`,
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching upcoming jobs:', error);
    throw error;
  }
};

/**
 * Get single-day jobs
 * @returns {Promise<Array>} Single-day jobs data
 */
export const getSingleDayJobs = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/single-day-jobs`,
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching single-day jobs:', error);
    throw error;
  }
};

/**
 * Get recent estimates
 * @returns {Promise<Array>} Recent estimates data
 */
export const getRecentEstimates = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/recent-estimates`,
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching recent estimates:', error);
    throw error;
  }
};

/**
 * Get recent sent estimates
 * @returns {Promise<Array>} Recent sent estimates data
 */
export const getRecentSentEstimates = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/recent-sent-estimates`,
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching recent sent estimates:', error);
    throw error;
  }
};

export const getStaleEstimates = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/stale-estimates`,
      getHeaders()
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching stale estimates:', error);
    throw error;
  }
};